/**
 * Lazy Loading de Imagens
 * Carrega imagens sob demanda usando IntersectionObserver
 */

export class LazyLoading {
    constructor(options = {}) {
        this.options = {
            selector: 'img[data-src]',
            rootMargin: '50px',
            threshold: 0.01,
            ...options
        };
        
        this.observer = null;
        this.images = [];
        this.observed_images = new WeakSet();
        this.init();
    }

    init() {
        if (!('IntersectionObserver' in window)) {
            console.warn('IntersectionObserver não suportado. Lazy loading desabilitado.');
            this.loadAllImmediately();
            return;
        }

        this.setupObserver();
        this.observeImages();
    }

    setupObserver() {
        this.observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    this.loadImage(entry.target);
                    this.observer.unobserve(entry.target);
                }
            });
        }, {
            rootMargin: this.options.rootMargin,
            threshold: this.options.threshold
        });
    }

    observeImages() {
        this.images = document.querySelectorAll(this.options.selector);
        this.images.forEach(img => {
            const source = img.getAttribute('src');
            const lazy_source = img.getAttribute('data-src');

            // Não observa novamente imagens que já receberam a URL definitiva.
            if (source && lazy_source && source === lazy_source) {
                if (img.complete && img.naturalWidth > 0) {
                    img.classList.remove('lazy');
                    img.classList.add('loaded');
                } else {
                    img.addEventListener('load', () => {
                        img.classList.remove('lazy');
                        img.classList.add('loaded');
                    }, { once: true });
                }

                return;
            }

            if (this.observed_images.has(img)) {
                return;
            }

            this.observed_images.add(img);
            this.observer.observe(img);
        });
    }

    loadImage(img) {
        const src = img.dataset.src;
        const srcset = img.dataset.srcset;
        
        // Carregar imagem
        img.onload = () => {
            img.classList.remove('lazy');
            img.classList.add('loaded');
        };
        
        img.onerror = () => {
            img.classList.add('error');
            console.warn(`Erro ao carregar: ${src}`);
        };
        
        if (src) img.src = src;
        if (srcset) img.srcset = srcset;

        img.removeAttribute('data-src');
        img.removeAttribute('data-srcset');
    }

    loadAllImmediately() {
        // Fallback para navegadores sem IntersectionObserver
        const images = document.querySelectorAll(this.options.selector);
        images.forEach(img => this.loadImage(img));
    }

    refresh() {
        this.observeImages();
    }

    destroy() {
        if (this.observer) {
            this.observer.disconnect();
        }
    }
}

export default LazyLoading;
